import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { formatNotApplicable } from '@/helpers/format-empty';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';

import type { CompanyRatingType } from '../company.state';

import { RankingStarIcon } from './ranking-star-icon';
import { RankingStarInput } from './ranking-star-input';
import { Loader } from '@/components/loader/loader';

const USER_RANKING_STAR_SIZE = '20px';
const AVERAGE_TEXT_HEIGHT = '28px';
const DEFAULT_CHILD_GAP = '12px';
const COMPACT_CHILD_GAP = '4px';

export const CompanyRatingCard = ({
  disabled,
  type,
  rating,
  initialValue,
  onChange,
  loading,
  hideRankingAverage,
}: {
  disabled?: boolean;
  type: CompanyRatingType;
  rating: number | null;
  initialValue?: number | null;
  onChange: (value: number) => void;
  loading?: boolean;
  hideRankingAverage?: boolean;
}) => {
  const { t } = useTranslation('companies');

  return (
    <RankingCardContainer data-testid={`ranking-card-container-${type}`}>
      <Stack gap={hideRankingAverage ? COMPACT_CHILD_GAP : DEFAULT_CHILD_GAP}>
        <Title>{t(`ranking.${type}`)}</Title>
        {!hideRankingAverage &&
          (loading ? (
            <Loader />
          ) : (
            <AveratingRatingContainer name="ranking-card-average" gap="8px">
              <AveratingRating data-testid="average-rating">
                {formatNotApplicable(rating?.toFixed(1))}
              </AveratingRating>
              <RankingStarIcon filled size={USER_RANKING_STAR_SIZE} />
            </AveratingRatingContainer>
          ))}
        <Flexbox
          name="ranking-user-input"
          gap="15px"
          alignItems="center"
          justify="space-between"
          wrap="wrap"
        >
          <YourRanking>{t`ranking.yourRanking`}</YourRanking>
          <RankingStarInput
            disabled={disabled}
            initialValue={initialValue ? Number(initialValue) : undefined}
            onChange={onChange}
          />
        </Flexbox>
      </Stack>
    </RankingCardContainer>
  );
};

const RankingCardContainer = styled('div')`
  background-color: ${({ theme }) =>
    theme.palettes.component.company.ranking.cardBackground};
  border-radius: 16px;
  padding: 12px 16px;
  width: 100%;
`;

const AveratingRatingContainer = styled(Flexbox)`
  height: ${AVERAGE_TEXT_HEIGHT};
`;

const Title = styled('p')`
  ${({ theme }) => theme.typography.companyRanking.card.title};
`;

const YourRanking = styled('p')`
  ${({ theme }) => theme.typography.companyRanking.card.yourRanking};
`;

const AveratingRating = styled('p')`
  ${({ theme }) => theme.typography.companyRanking.card.averageRating};
`;
